/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import type { BasicFunction } from '../../helpers/jsHelpers/types';

import { isValidOtp } from '../modal/pinModal/helpers';
import './CodePrompt.css';
import { CodeDisplayType, DIGIT_INITIAL_VALUE } from './constsAndTypes';

export type CodePromptHandle = {
  clear: BasicFunction,
  giveFocus: BasicFunction,
  removeFocus: BasicFunction,
};

type CodePromptPropType = {
  +allowPaste?: boolean,
  +ariaDescribedBy?: string,
  +ariaInvalid?: boolean,
  +ariaLabel?: string,
  +displayType: CodeDisplayType,
  +hasFocus?: boolean,
  +length: number,
  +name?: string,
  +onChange: (value: string, done: boolean) => void,
  +onFocused?: (name?: string) => void,
};

const buildInitialValues = (length: number): Array<string> => Array.from({ length }).map(() => DIGIT_INITIAL_VALUE);

const CodePrompt = (
  { allowPaste = false, ariaDescribedBy, ariaInvalid, ariaLabel, displayType, hasFocus = false, length, name, onChange, onFocused }: CodePromptPropType,
  ref: React.RefSetter<CodePromptHandle>,
): React.Node => {
  const [values, setValues] = useState<Array<string>>(buildInitialValues(length));
  const [isFocused, setIsFocused] = useState<boolean>(hasFocus);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const giveFocus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const removeFocus = useCallback(() => {
    inputRef.current?.blur();
  }, []);

  const clear = useCallback(() => {
    setValues(buildInitialValues(length));
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [length]);

  useImperativeHandle(ref, (): CodePromptHandle => {
    return {
      clear,
      giveFocus,
      removeFocus,
    };
  });

  useEffect(() => {
    if (hasFocus) {
      giveFocus();
    }
  }, [hasFocus, giveFocus]);

  const handleInputChange = useCallback(
    (event: SyntheticInputEvent<HTMLInputElement>) => {
      const rawValue = event.currentTarget.value.replace(/\D/gu, '').slice(0, length);
      const newValues = buildInitialValues(length);

      for (let i = 0; i < rawValue.length; i += 1) {
        newValues[i] = rawValue[i];
      }

      setValues(newValues);
      const isDone = rawValue.length === length;
      onChange(rawValue, isDone);
    },
    [length, onChange],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);

    if (onFocused !== undefined) {
      onFocused(name);
    }
  }, [name, onFocused]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handleContainerClick = useCallback(() => {
    giveFocus();
  }, [giveFocus]);

  const handlePaste = useCallback(
    (event: SyntheticClipboardEvent<HTMLInputElement>) => {
      // Always prevent native paste behavior
      event.preventDefault();
      event.stopPropagation();

      if (!allowPaste) {
        return;
      }

      const text = event.clipboardData.getData('text');
      if (isValidOtp(text)) {
        const sliced = text.slice(0, length);
        setValues(sliced.split(''));

        if (inputRef.current) {
          inputRef.current.value = sliced;
        }

        onChange(sliced, sliced.length === length);
      }
    },
    [allowPaste, length, onChange],
  );

  const activeIndex = Math.min(
    values.findIndex((v) => v === DIGIT_INITIAL_VALUE),
    length - 1,
  );
  const currentFocusIndex = activeIndex === -1 ? length - 1 : activeIndex;

  return (
    <div className='digitsWrapper' onClick={handleContainerClick}>
      <input
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel || name}
        className='hiddenCodeInput'
        inputMode='numeric'
        maxLength={length}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onPaste={handlePaste}
        pattern='[0-9]*'
        ref={inputRef}
        type='text'
      />
      {values.map((value, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={clsx('digit', isFocused && currentFocusIndex === i && 'focused')} key={i}>
          {value === DIGIT_INITIAL_VALUE || displayType === CodeDisplayType.Visible ? value : <>&bull;</>}
        </div>
      ))}
    </div>
  );
};

export default React.forwardRef<CodePromptPropType, CodePromptHandle>(CodePrompt) as React.ComponentType<{
  ...CodePromptPropType,
  +ref?: React.RefSetter<CodePromptHandle> | { current: CodePromptHandle | null, ... } | null,
}>;
